// src/pages/MailSettings.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {

  Alert, Button, Card, Col, Empty, Form, Input, Modal, Popconfirm,

  Row, Select, Space, Spin, Table, Typography, message,

} from "antd";

import {

  DeleteOutlined, MailOutlined, PlusOutlined, ReloadOutlined,

  SaveOutlined, SettingOutlined,

} from "@ant-design/icons";

import { BASE_URL } from "../services/BaseUrl";

import "../css/sm-art-common.css";

const ROOT = new URL("/index.php/backlog/", BASE_URL.BASE).toString();

const DEFAULT_GROUP = "finishing_backlog_analytics";

const { Title, Text } = Typography;

const validEmail = (value) => /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(value);

const validGroupName = (value) => /^[A-Za-z0-9_-]{1,50}$/.test(value);

async function mailApi(path, { body, signal, query } = {}) {

  const url = new URL(path, ROOT);

  Object.entries(query || {}).forEach(([key, value]) => {

    if (value !== null && value !== undefined) url.searchParams.set(key, String(value));

  });

  const response = await fetch(url.toString(), {

    ...(body !== undefined ? {

      method: "POST",

      headers: { "Content-Type": "application/json" },

      body: JSON.stringify(body),

    } : {}),

    signal,

  });

  const result = await response.json().catch(() => null);

  if (!response.ok || result?.status !== true) {

    const reason = result?.data?.invalid?.length

      ? ` Invalid: ${result.data.invalid.join(", ")}` : "";

    throw new Error(`${result?.message || `Request failed (HTTP ${response.status})`}${reason}`);

  }

  return result;

}

const normalizeGroup = (item) => ({

  ...item,

  e_mails: Array.isArray(item?.e_mails)

    ? item.e_mails

    : String(item?.e_mails || "").split(/[,;\s]+/).filter(Boolean),

});

export default function MailSettings() {

  const [groups, setGroups] = useState([]);

  const [selectedName, setSelectedName] = useState(DEFAULT_GROUP);

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [removing, setRemoving] = useState("");

  const [error, setError] = useState("");

  const [email, setEmail] = useState("");

  const [createOpen, setCreateOpen] = useState(false);

  const [newName, setNewName] = useState("");

  const [newEmails, setNewEmails] = useState("");

  const [replaceOpen, setReplaceOpen] = useState(false);

  const [replaceText, setReplaceText] = useState("");

  const [groupMissing, setGroupMissing] = useState(false);

  const selected = useMemo(

    () => groups.find((g) => g.description === selectedName) || null,

    [groups, selectedName]

  );

  const refresh = useCallback(async (signal) => {

    setLoading(true);

    setError("");

    try {

      const response = await mailApi("mail_groups", { signal });

      if (!Array.isArray(response.data?.groups)) throw new Error("Invalid mail_groups response");

      const list = response.data.groups.map(normalizeGroup);

      setGroups(list);

      setSelectedName((previous) =>

        list.some((group) => group.description === previous)

          ? previous

          : list.some((group) => group.description === DEFAULT_GROUP)

            ? DEFAULT_GROUP

            : list[0]?.description || DEFAULT_GROUP

      );

      setGroupMissing(list.length === 0);

    } catch (err) {

      if (err.name !== "AbortError") setError(err.message || "Could not load mail groups");

    } finally {

      if (!signal?.aborted) setLoading(false);

    }

  }, []);

  useEffect(() => {

    const controller = new AbortController();

    refresh(controller.signal);

    return () => controller.abort();

  }, [refresh]);

  const updateGroupFromResponse = (response) => {

    const group = response?.data?.group;

    if (group?.description) {

      const next = normalizeGroup(group);

      setGroups((old) => {

        const remaining = old.filter((g) => g.m_id !== next.m_id && g.description !== next.description);

        return [...remaining, next].sort((a, b) => a.description.localeCompare(b.description));

      });

      setSelectedName(next.description);

      setGroupMissing(false);

    }

    message.success(response?.message || "Saved");

  };

  const addEmail = async () => {

    const clean = email.trim().toLowerCase();

    if (!selected) return message.warning("Select or create a mail group first");

    if (!validEmail(clean)) return message.error("Enter a valid email address");

    if (selected.e_mails.some((address) => address.toLowerCase() === clean)) {

      return message.info("Already in the group");

    }

    setSaving(true);

    try {

      const response = await mailApi("add_mail_email", {

        body: { description: selected.description, email: clean },

      });

      updateGroupFromResponse(response);

      setEmail("");

    } catch (err) { message.error(err.message); }

    finally { setSaving(false); }

  };

  const removeEmail = async (address) => {

    if (!selected) return;

    setRemoving(address);

    try {

      const response = await mailApi("remove_mail_email", {

        body: { description: selected.description, email: address },

      });

      updateGroupFromResponse(response);

    } catch (err) { message.error(err.message); }

    finally { setRemoving(""); }

  };

  const createGroup = async () => {

    const name = newName.trim();

    const addresses = [...new Set(newEmails.split(/[,;\s]+/).map((a) => a.toLowerCase().trim()).filter(Boolean))];

    if (!validGroupName(name)) return message.error("Group name: up to 50 letters, numbers, underscores or hyphens");

    if (!addresses.length || addresses.some((address) => !validEmail(address))) {

      return message.error("Provide at least one valid email address");

    }

    setSaving(true);

    try {

      const response = await mailApi("save_mail_group", {

        body: { description: name, e_mails: addresses },

      });

      updateGroupFromResponse(response);

      setCreateOpen(false);

      setNewName("");

      setNewEmails("");

    } catch (err) { message.error(err.message); }

    finally { setSaving(false); }

  };

  const replaceAddresses = async () => {

    if (!selected) return;

    const addresses = [...new Set(replaceText.split(/[,;\s]+/).map((a) => a.toLowerCase().trim()).filter(Boolean))];

    if (!addresses.length || addresses.some((a) => !validEmail(a))) {

      return message.error("At least one valid email address is required");

    }

    setSaving(true);

    try {

      const response = await mailApi("save_mail_group", {

        body: { m_id: selected.m_id, description: selected.description, e_mails: addresses },

      });

      updateGroupFromResponse(response);

      setReplaceOpen(false);

    } catch (err) { message.error(err.message); }

    finally { setSaving(false); }

  };

  const columns = [

    { title: "Sl.No.", key: "no", width: 126, render: (_v, _r, index) => index + 1 },

    { title: "Email Address", dataIndex: "email", key: "email", align: "center", render: (value) => <Text>{value}</Text> },

    {

      title: "Action", key: "action", width: 130, align: "center",

      render: (_v, record) => (

        <Popconfirm

          title="Remove this recipient?"

          description="This email will no longer receive reports sent to this group."

          okText="Remove" okButtonProps={{ danger: true }}

          onConfirm={() => removeEmail(record.email)}

          disabled={selected?.e_mails.length <= 1}

        >

          <Button danger size="small" icon={<DeleteOutlined />}

            disabled={selected?.e_mails.length <= 1 || saving}

            loading={removing === record.email}>Remove</Button>

        </Popconfirm>

      ),

    },

  ];

  return (

    <div className="sm-page unified-report-page mail-settings-page">

      <div className="sm-page-inner">

        <section className="unified-panel">

          <header className="unified-heading mail-settings-heading">

            <div>

              <Title level={3} className="sm-page-title"><SettingOutlined /> Mail Settings</Title>

              <Text type="secondary">Manage report email groups and their recipients.</Text>

            </div>

            <Space wrap>

              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => refresh()}>Refresh</Button>

              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Create Group</Button>

            </Space>

          </header>

          {error && <Alert showIcon type="error" message={error} style={{ marginBottom: 16 }} />}

          <Spin spinning={loading}>

            <Card bordered className="mail-settings-card">

              <Row gutter={[16, 16]} align="stretch" className="mail-settings-overview">

                <Col xs={24} lg={16}>
                  <div className="mail-settings-group-field">

                  <Text strong>Mail Group</Text>

                  <Select

                    value={selected ? selectedName : undefined}

                    style={{ width: "100%", marginTop: 8 }}

                    placeholder="Select a mail group"

                    options={groups.map((g) => ({ value: g.description, label: g.description }))}

                    onChange={(name) => { setSelectedName(name); setEmail(""); }}

                    showSearch optionFilterProp="label"

                  />
                  </div>

                </Col>

                <Col xs={24} lg={8}>

                  {selected && (

                    <div className="mail-settings-counter">
                      <div className="mail-settings-stat"><span>Recipients</span><strong>{selected.email_count ?? selected.e_mails.length}</strong></div>
                      <div className="mail-settings-stat"><span>Storage used</span><strong>{selected.length_used ?? selected.e_mails.join(",").length} / {selected.length_max ?? 250}</strong></div>
                    </div>

                  )}

                </Col>

              </Row>

              {selected?.description === DEFAULT_GROUP && (

                <Alert style={{ marginTop: 18 }} type="info" showIcon

                  message="Finishing Backlog Analytics"

                  description="Send Mail uses the recipient list in this group. The dashboard never sends addresses in the request." />

              )}

              {selected ? (

                <>

                  <div className="mail-settings-section-head">

                    <Title level={5} style={{ margin: 0 }}>Recipients</Title>

                    <Button onClick={() => { setReplaceText(selected.e_mails.join("\n")); setReplaceOpen(true); }} disabled={saving || Boolean(removing)} icon={<SaveOutlined />}>Edit Full List</Button>

                  </div>

                  <div className="mail-settings-add-row">

                    <Input

                      prefix={<MailOutlined />} placeholder="Enter an email address"

                      value={email} onChange={(e) => setEmail(e.target.value)}

                      onPressEnter={addEmail} maxLength={254} disabled={saving || Boolean(removing)}

                    />

                    <Button type="primary" icon={<PlusOutlined />} onClick={addEmail}

                      disabled={!email.trim() || Boolean(removing)} loading={saving}>Add Email</Button>

                  </div>

                  <Table

                    className="unified-table mail-settings-table"

                    rowKey="email" size="small" columns={columns}

                    dataSource={selected.e_mails.map((address) => ({ email: address }))}

                    pagination={selected.e_mails.length > 10 ? { pageSize: 10 } : false}

                    scroll={{ x: 500 }}

                  />

                  <div className="mail-settings-hint">At least one recipient must remain in each group. Changes to recipients are saved immediately.</div>

                </>

              ) : (

                <Empty style={{ marginTop: 24 }} description={groupMissing ? "No mail groups yet. Create your first group." : "Select a mail group"} />

              )}

            </Card>

          </Spin>

        </section>

      </div>

      <Modal title="Create Mail Group" open={createOpen} onCancel={() => setCreateOpen(false)}

        onOk={createGroup} okText="Save Group" confirmLoading={saving} destroyOnClose>

        <Form layout="vertical">

          <Form.Item label="Group name" required extra="Letters, numbers, underscores and hyphens only (maximum 50).">

            <Input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={50} placeholder="example_report_group" />

          </Form.Item>

          <Form.Item label="Email addresses" required extra="Separate addresses with commas, semicolons, spaces or new lines.">

            <Input.TextArea rows={4} value={newEmails} onChange={(e) => setNewEmails(e.target.value)} placeholder="one@example.com\ntwo@example.com" />

          </Form.Item>

        </Form>

      </Modal>

      <Modal title={`Edit Recipients — ${selected?.description || ""}`} open={replaceOpen}

        onCancel={() => setReplaceOpen(false)} onOk={replaceAddresses}

        okText="Replace Full List" confirmLoading={saving} destroyOnClose>

        <Alert type="warning" showIcon style={{ marginBottom: 14 }}

          message="This replaces the entire recipient list for this group." />

        <Input.TextArea rows={8} value={replaceText} onChange={(e) => setReplaceText(e.target.value)} />

        <Text type="secondary">One email per line, or comma-separated. Existing addresses not included here will be removed.</Text>

      </Modal>

    </div>

  );

}
